/**
 * /projects/new — create a project. Minimal form (name + optional
 * description); on success navigates to the new project's detail page.
 */

import { useState } from "react";
import { Button, Group, Paper, Stack, Textarea, TextInput, Title } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { createProject, type CreateProjectIn } from "@/shared/api/projects";

export function NewProject() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const valid = name.trim().length > 0;

  const onSave = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      const input: CreateProjectIn = {
        name: name.trim(),
        description: description.trim() || null,
      };
      const created = await createProject(token, input);
      toastSuccess("Project created", `${created.name} is ready.`);
      navigate(`/projects/${created.project_id}`);
    } catch (err) {
      toastApiError("Could not create project", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md" maw={640}>
      <Title order={2}>New project</Title>
      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="eTMF Automation"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Description"
            placeholder="Optional"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={() => void onSave()}
              loading={busy}
              disabled={!valid || busy}
            >
              Create project
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
